import React, { useState, useEffect } from 'react';
import API, { UPLOADS_URL } from '../api/axios';
import PageBanner from '../components/PageBanner';

const Fees = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, sectionRes] = await Promise.all([
          API.get('/pages/public/fees'),
          API.get('/home-sections?keys=page_fees')
        ]);
        setPage(pageRes.data.data);
        if (sectionRes.data?.data?.page_fees?.fee_structure) {
          setData(sectionRes.data.data.page_fees.fee_structure);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>;
  if (!data) return <div className="container py-20 text-center">Data not found.</div>;

  return (
    <>
      <PageBanner 
        title={page?.banner_heading || 'Fee Structure'} 
        subtitle={page?.banner_sub_heading || ""} 
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: page?.title || 'Fees' }]} 
        backgroundImage={page?.banner_image ? `${UPLOADS_URL}/${page.banner_image}` : null} 
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/why-choose-us.jpg`} 
      />

      <div className="container mx-auto px-[15px] py-12 space-y-12">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-primary mb-4">Academic Session {data.session}</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full"></div>
        </div>

        {/* One Time Fees */}
        {data.one_time_fees && data.one_time_fees.length > 0 && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-money-bill-wave text-primary bg-primary/10 w-10 h-10 flex items-center justify-center rounded-xl text-lg"></i> 
              One Time Fees
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="p-4 font-bold text-gray-700">Fee Type</th>
                    <th className="p-4 font-bold text-gray-700">Amount</th>
                    <th className="p-4 font-bold text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.one_time_fees.map((fee, idx) => (
                    <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="p-4 font-medium text-gray-800">{fee.fee_type}</td>
                      <td className="p-4 font-bold text-primary">{fee.currency} {fee.amount}</td>
                      <td className="p-4 text-gray-500 text-sm">{fee.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Composite Fees */}
        {data.composite_fees && data.composite_fees.length > 0 && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden">
             <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full opacity-50 z-0"></div>
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <i className="fas fa-layer-group text-primary bg-primary/10 w-10 h-10 flex items-center justify-center rounded-xl text-lg"></i> 
                Composite Fees (Quarterly)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <th className="p-4 font-bold text-gray-700 whitespace-nowrap">Class</th>
                      <th className="p-4 font-bold text-gray-700 text-center">Quarter 1</th>
                      <th className="p-4 font-bold text-gray-700 text-center">Quarter 2</th>
                      <th className="p-4 font-bold text-gray-700 text-center">Quarter 3</th>
                      <th className="p-4 font-bold text-gray-700 text-center">Quarter 4</th>
                      <th className="p-4 font-bold text-gray-800 text-center bg-gray-100">Total (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.composite_fees.map((fee, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50/50 transition">
                        <td className="p-4 font-bold text-gray-800 whitespace-nowrap">{fee.class}</td>
                        <td className="p-4 text-gray-600 text-center font-medium">{fee.quarter_1}</td>
                        <td className="p-4 text-gray-600 text-center font-medium">{fee.quarter_2}</td>
                        <td className="p-4 text-gray-600 text-center font-medium">{fee.quarter_3}</td>
                        <td className="p-4 text-gray-600 text-center font-medium">{fee.quarter_4}</td>
                        <td className="p-4 font-bold text-primary text-center bg-blue-50/30">{fee.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Additional Charges */}
        {data.additional_charges && data.additional_charges.length > 0 && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-plus-circle text-primary bg-primary/10 w-10 h-10 flex items-center justify-center rounded-xl text-lg"></i> 
              Additional Charges
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {data.additional_charges.map((charge, idx) => (
                <div key={idx} className="p-5 border border-gray-100 rounded-xl bg-gray-50 flex flex-col justify-between">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-bold text-lg text-gray-800">{charge.fee_type}</h4>
                      <span className="text-sm text-gray-500">{charge.applicable_grades}</span>
                    </div>
                    {charge.is_optional && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">Optional</span>
                    )}
                  </div>
                  
                  <div className="flex gap-4 border-t border-gray-200 pt-4 mt-auto">
                    {charge.quarterly_amount && (
                      <div>
                        <p className="text-xs text-gray-500 font-semibold mb-1 uppercase">Quarterly</p>
                        <p className="font-bold text-gray-800">INR {charge.quarterly_amount}</p>
                      </div>
                    )}
                    {charge.annually_amount && (
                      <div>
                        <p className="text-xs text-gray-500 font-semibold mb-1 uppercase">Yearly</p>
                        <p className="font-bold text-primary">INR {charge.annually_amount}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Rules */}
        {data.payment_rules && (
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-info-circle text-primary bg-primary/10 w-10 h-10 flex items-center justify-center rounded-xl text-lg"></i> 
              Important Payment Rules
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {data.payment_rules.due_date && (
                <div className="flex items-start gap-4 p-4 rounded-xl bg-blue-50/50 border border-blue-100">
                  <i className="fas fa-calendar-alt text-blue-500 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-blue-900 text-sm mb-1">Due Date</h4>
                    <p className="text-blue-800/80 text-sm">{data.payment_rules.due_date}</p>
                  </div>
                </div>
              )}
              {data.payment_rules.late_fine && (
                <div className="flex items-start gap-4 p-4 rounded-xl bg-red-50/50 border border-red-100">
                  <i className="fas fa-exclamation-triangle text-red-500 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-red-900 text-sm mb-1">Late Fine</h4>
                    <p className="text-red-800/80 text-sm">{data.payment_rules.late_fine}</p>
                  </div>
                </div>
              )}
              {data.payment_rules.sibling_concession && (
                <div className="flex items-start gap-4 p-4 rounded-xl bg-green-50/50 border border-green-100">
                  <i className="fas fa-user-friends text-green-500 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-green-900 text-sm mb-1">Sibling Concession</h4>
                    <p className="text-green-800/80 text-sm">{data.payment_rules.sibling_concession}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Fees;
